/**
 * ProjectSectionConfigPage — PM sets up which section types apply to a project
 * before analysis runs.
 *
 * Mounted at /v2/projects/:id/section-config.
 *
 * Layout:
 *   Top: sticky header with back link + project name
 *   Left/main: project's current section config entries (editable)
 *   Bottom panel: registry browser — click "Add" to copy an entry into config
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  ChevronDown,
  ChevronUp,
  Layout,
  Pencil,
  Plus,
  Search,
  Trash2,
  UploadCloud,
  X,
} from "lucide-react";
import { v2Api } from "@/lib/v2Api";
import { confirm } from "@/lib/confirm";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// ── helpers ──────────────────────────────────────────────────────────────────

function toSlug(s) {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "");
}

function KeywordTokens({ keywords, editable, onChange }) {
  const [draft, setDraft] = useState("");
  const inputRef = useRef(null);

  function addToken(raw) {
    const token = raw.trim().toLowerCase();
    if (!token) return;
    if (!keywords.includes(token)) onChange([...keywords, token]);
    setDraft("");
  }

  function removeToken(kw) {
    onChange(keywords.filter((k) => k !== kw));
  }

  return (
    <div className="flex flex-wrap gap-1 items-center">
      {keywords.map((kw) => (
        <Badge key={kw} variant="secondary" className="text-xs gap-1 pr-1">
          {kw}
          {editable && (
            <button
              onClick={() => removeToken(kw)}
              className="hover:text-red-600 ml-0.5"
            >
              <X className="w-3 h-3" />
            </button>
          )}
        </Badge>
      ))}
      {editable && (
        <input
          ref={inputRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (["Enter", ",", "Tab"].includes(e.key)) {
              e.preventDefault();
              addToken(draft);
            }
          }}
          onBlur={() => addToken(draft)}
          placeholder={keywords.length === 0 ? "Type keyword, press Enter…" : "+"}
          className="text-xs border border-dashed border-slate-300 rounded px-1.5 py-0.5 w-28 focus:outline-none focus:border-blue-400 bg-transparent"
        />
      )}
    </div>
  );
}

// ── Section entry form (create / edit) ───────────────────────────────────────

const EMPTY_FORM = {
  code: "",
  name: "",
  description: "",
  keyword_patterns: [],
  dept_id: "",
  secondary_dept_ids: [],
};

function SectionForm({ initial, departments, onSave, onCancel, loading }) {
  const [form, setForm] = useState(initial ?? EMPTY_FORM);
  const codeAutoFilled = useRef(false);

  function set(key, val) {
    setForm((prev) => ({ ...prev, [key]: val }));
  }

  function handleNameChange(val) {
    set("name", val);
    if (!codeAutoFilled.current || form.code === "") {
      set("code", toSlug(val));
    }
  }

  function handleCodeChange(val) {
    codeAutoFilled.current = true;
    set("code", toSlug(val));
  }

  const valid = form.name.trim() && form.code.trim();

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-medium text-slate-600">Name *</label>
          <Input
            value={form.name}
            onChange={(e) => handleNameChange(e.target.value)}
            placeholder="e.g. Earnest Money Deposit"
            className="h-8 text-sm mt-0.5"
          />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600">Code *</label>
          <Input
            value={form.code}
            onChange={(e) => handleCodeChange(e.target.value)}
            placeholder="e.g. emd"
            className="h-8 text-sm mt-0.5 font-mono"
          />
        </div>
      </div>
      <div>
        <label className="text-xs font-medium text-slate-600">
          Description
        </label>
        <Input
          value={form.description}
          onChange={(e) => set("description", e.target.value)}
          placeholder="Optional — shown to team members"
          className="h-8 text-sm mt-0.5"
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-medium text-slate-600 block mb-1">
            Primary department
            <span className="ml-1 text-slate-400 font-normal">(accountable)</span>
          </label>
          <Select
            value={form.dept_id || "__none__"}
            onValueChange={(v) => set("dept_id", v === "__none__" ? "" : v)}
          >
            <SelectTrigger className="h-8 text-sm">
              <SelectValue placeholder="None" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">None</SelectItem>
              {departments.map((d) => (
                <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600 block mb-1">
            Secondary departments
            <span className="ml-1 text-slate-400 font-normal">(co-contribute)</span>
          </label>
          <div className="flex flex-wrap gap-1 min-h-8 border border-slate-200 rounded-md px-2 py-1 bg-white">
            {(form.secondary_dept_ids || []).map((id) => {
              const d = departments.find((x) => x.id === id);
              return d ? (
                <span key={id} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs bg-blue-50 text-blue-700 border border-blue-200">
                  {d.name}
                  <button type="button" className="hover:text-blue-900"
                    onClick={() => set("secondary_dept_ids", form.secondary_dept_ids.filter((x) => x !== id))}>
                    ×
                  </button>
                </span>
              ) : null;
            })}
            <select
              className="text-xs border-0 outline-none bg-transparent text-slate-500 flex-1 min-w-16"
              value=""
              onChange={(e) => {
                const v = e.target.value;
                if (!v || (form.secondary_dept_ids || []).includes(v) || v === form.dept_id) return;
                set("secondary_dept_ids", [...(form.secondary_dept_ids || []), v]);
              }}
            >
              <option value="">+ add</option>
              {departments
                .filter((d) => d.id !== form.dept_id && !(form.secondary_dept_ids || []).includes(d.id))
                .map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </div>
        </div>
      </div>
      <div>
        <label className="text-xs font-medium text-slate-600 block mb-1">
          Keywords (press Enter to add)
        </label>
        <KeywordTokens
          keywords={form.keyword_patterns}
          editable
          onChange={(kw) => set("keyword_patterns", kw)}
        />
      </div>
      <div className="flex justify-end gap-2 pt-1">
        <Button variant="outline" size="sm" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          size="sm"
          disabled={!valid || loading}
          onClick={() => onSave(form)}
        >
          {loading ? "Saving…" : "Save"}
        </Button>
      </div>
    </div>
  );
}

// ── Config entry row ──────────────────────────────────────────────────────────

function ConfigRow({
  entry,
  departments,
  onEdit,
  onDelete,
  onPushToRegistry,
  isInRegistry,
}) {
  const primaryDept = departments.find((d) => d.id === entry.dept_id);
  const secondaryDepts = (entry.secondary_dept_ids || [])
    .map((id) => departments.find((d) => d.id === id))
    .filter(Boolean);

  return (
    <div className="flex items-start justify-between gap-4 p-3 rounded-lg border bg-white hover:border-slate-300 transition-colors">
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-sm text-slate-900">{entry.name}</span>
          <span className="font-mono text-xs text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">
            {entry.code}
          </span>
          {primaryDept && (
            <Badge variant="outline" className="text-xs border-slate-400 text-slate-700"
              title="Primary department">
              {primaryDept.name}
            </Badge>
          )}
          {secondaryDepts.map((d) => (
            <Badge key={d.id} variant="outline"
              className="text-xs border-blue-200 text-blue-600 bg-blue-50/50"
              title="Secondary department">
              {d.name}
            </Badge>
          ))}
          {entry.pushed_back && (
            <Badge className="text-xs bg-green-50 text-green-700 border-green-200">
              In registry
            </Badge>
          )}
        </div>
        {entry.description && (
          <p className="text-xs text-slate-500">{entry.description}</p>
        )}
        <KeywordTokens
          keywords={entry.keyword_patterns ?? []}
          editable={false}
        />
      </div>
      <div className="flex items-center gap-1 shrink-0">
        {!entry.pushed_back && !isInRegistry && (
          <Button
            size="sm"
            variant="ghost"
            className="h-7 text-xs text-blue-600 hover:bg-blue-50"
            title="Add to workspace registry"
            onClick={() => onPushToRegistry(entry)}
          >
            <UploadCloud className="w-3.5 h-3.5 mr-1" />
            Add to registry
          </Button>
        )}
        <Button
          size="sm"
          variant="ghost"
          className="h-7 w-7 p-0"
          onClick={() => onEdit(entry)}
        >
          <Pencil className="w-3.5 h-3.5" />
        </Button>
        <Button
          size="sm"
          variant="ghost"
          className="h-7 w-7 p-0 text-red-500 hover:bg-red-50"
          onClick={() => onDelete(entry)}
        >
          <Trash2 className="w-3.5 h-3.5" />
        </Button>
      </div>
    </div>
  );
}

// ── Registry browser row ──────────────────────────────────────────────────────

function RegistryRow({ entry, departments, onAdd, alreadyAdded }) {
  const dept = departments.find((d) => d.id === entry.dept_id);
  return (
    <div className="flex items-start justify-between gap-4 p-3 rounded-lg border bg-slate-50">
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-sm text-slate-800">{entry.name}</span>
          <span className="font-mono text-xs text-slate-400 bg-white border px-1.5 py-0.5 rounded">
            {entry.code}
          </span>
          {dept && (
            <Badge variant="outline" className="text-xs">
              {dept.name}
            </Badge>
          )}
          {entry.is_required && (
            <Badge className="text-xs bg-amber-50 text-amber-700 border-amber-200">
              Required
            </Badge>
          )}
        </div>
        {entry.description && (
          <p className="text-xs text-slate-500">{entry.description}</p>
        )}
        <div className="flex flex-wrap gap-1">
          {(entry.keyword_patterns ?? []).slice(0, 6).map((kw) => (
            <Badge key={kw} variant="secondary" className="text-xs">
              {kw}
            </Badge>
          ))}
          {(entry.keyword_patterns ?? []).length > 6 && (
            <span className="text-xs text-slate-400">
              +{entry.keyword_patterns.length - 6} more
            </span>
          )}
        </div>
      </div>
      <Button
        size="sm"
        variant={alreadyAdded ? "ghost" : "outline"}
        disabled={alreadyAdded}
        className={`shrink-0 h-7 text-xs ${
          alreadyAdded ? "text-slate-400" : "text-blue-600 border-blue-200 hover:bg-blue-50"
        }`}
        onClick={() => !alreadyAdded && onAdd(entry)}
      >
        {alreadyAdded ? "Added" : <><Plus className="w-3 h-3 mr-1" />Add</>}
      </Button>
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export default function ProjectSectionConfigPage({ projectId: projectIdProp, compact = false }) {
  const { id: routeProjectId } = useParams();
  const navigate = useNavigate();
  const projectId = projectIdProp ?? routeProjectId;

  const [project, setProject] = useState(null);
  const [configs, setConfigs] = useState([]);
  const [registry, setRegistry] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);

  // registry browser
  const [registryOpen, setRegistryOpen] = useState(true);
  const [registrySearch, setRegistrySearch] = useState("");

  // create/edit dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null); // null = create
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  const load = useCallback(() => {
    if (!projectId) return;
    Promise.all([
      v2Api.projects.get(projectId),
      v2Api.sectionConfig.list(projectId),
      v2Api.sectionRegistry.list(),
      v2Api.departments(),
    ])
      .then(([proj, cfgs, reg, depts]) => {
        setProject(proj);
        setConfigs(cfgs);
        setRegistry(reg);
        setDepartments(depts);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [projectId]);

  useEffect(() => {
    load();
  }, [load]);

  // codes already in project config
  const configCodes = new Set(configs.map((c) => c.code));
  // codes already pushed to registry (by code match)
  const registryCodes = new Set(registry.map((r) => r.code));

  // ── add from registry ─────────────────────────────────────────────────────
  async function handleAddFromRegistry(entry) {
    try {
      const created = await v2Api.sectionConfig.create(projectId, {
        code: entry.code,
        name: entry.name,
        description: entry.description,
        keyword_patterns: entry.keyword_patterns ?? [],
        dept_id: entry.dept_id ?? null,
        secondary_dept_ids: entry.secondary_dept_ids ?? [],
      });
      setConfigs((prev) => [...prev, created]);
    } catch (e) {
      setError(e?.response?.data?.detail ?? "Failed to add section.");
    }
  }

  // ── create / edit save ────────────────────────────────────────────────────
  async function handleSave(form) {
    setSaving(true);
    setError(null);
    try {
      const payload = {
        code: form.code.trim(),
        name: form.name.trim(),
        description: form.description?.trim() || null,
        keyword_patterns: form.keyword_patterns,
        dept_id: form.dept_id || null,
        secondary_dept_ids: form.secondary_dept_ids || [],
      };
      if (editingEntry) {
        const updated = await v2Api.sectionConfig.patch(
          projectId,
          editingEntry.id,
          payload,
        );
        setConfigs((prev) =>
          prev.map((c) => (c.id === updated.id ? updated : c)),
        );
      } else {
        const created = await v2Api.sectionConfig.create(projectId, payload);
        setConfigs((prev) => [...prev, created]);
      }
      setDialogOpen(false);
      setEditingEntry(null);
    } catch (e) {
      setError(e?.response?.data?.detail ?? "Failed to save section.");
    } finally {
      setSaving(false);
    }
  }

  // ── delete ────────────────────────────────────────────────────────────────
  async function handleDelete(entry) {
    if (!await confirm(`Remove "${entry.name}" from this project?`, { title: "Remove section", confirmLabel: "Remove" })) return;
    try {
      await v2Api.sectionConfig.remove(projectId, entry.id);
      setConfigs((prev) => prev.filter((c) => c.id !== entry.id));
    } catch (e) {
      setError(e?.response?.data?.detail ?? "Failed to delete section.");
    }
  }

  // ── push to registry ──────────────────────────────────────────────────────
  async function handlePushToRegistry(entry) {
    // Pre-validate before hitting the API
    if (!entry.keyword_patterns || entry.keyword_patterns.length === 0) {
      setError("Add at least one keyword pattern before pushing to the registry.");
      return;
    }
    if (entry.keyword_patterns.some((p) => p.trim().length < 2)) {
      setError("All keyword patterns must be at least 2 characters.");
      return;
    }
    setError(null);
    try {
      const result = await v2Api.sectionConfig.pushToRegistry(projectId, entry.id);
      if (result.action === "created") {
        setRegistry((prev) => [...prev, result.entry]);
      } else {
        // merged: update the existing registry entry in place
        setRegistry((prev) =>
          prev.map((r) => r.code === result.entry.code ? result.entry : r)
        );
      }
      setConfigs((prev) =>
        prev.map((c) => (c.id === entry.id ? { ...c, pushed_back: true } : c)),
      );
      const msg = result.action === "merged"
        ? "Keywords merged into existing registry entry."
        : "Added to workspace registry.";
      setSuccessMsg(msg);
      setTimeout(() => setSuccessMsg(null), 4000);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(detail ?? "Failed to add to registry.");
    }
  }

  // ── open edit dialog ──────────────────────────────────────────────────────
  function openEdit(entry) {
    setEditingEntry(entry);
    setDialogOpen(true);
  }

  function openCreate() {
    setEditingEntry(null);
    setDialogOpen(true);
  }

  // ── registry filtered list ────────────────────────────────────────────────
  const filteredRegistry = registry.filter((r) => {
    if (!registrySearch) return true;
    const q = registrySearch.toLowerCase();
    return (
      r.name.toLowerCase().includes(q) ||
      r.code.toLowerCase().includes(q) ||
      (r.description ?? "").toLowerCase().includes(q)
    );
  });

  // ── render ────────────────────────────────────────────────────────────────
  return (
    <div className={compact ? "rounded-lg border border-slate-200 bg-white p-4 space-y-4" : "min-h-screen bg-slate-50"}>
      {compact ? (
        <div className="flex items-start justify-between gap-3 pb-2 border-b border-slate-100">
          <div>
            <h2 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
              <Layout className="w-4 h-4 text-blue-600" />
              Section config
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Configure the sections this project should route through.
            </p>
          </div>
          <Button size="sm" onClick={openCreate} className="shrink-0">
            <Plus className="w-4 h-4 mr-1" />
            Custom section
          </Button>
        </div>
      ) : (
        <div className="px-6 py-4 border-b bg-white sticky top-0 z-10">
          <button
            onClick={() => navigate(`/v2/projects/${projectId}`)}
            className="text-xs text-slate-500 hover:text-slate-800 inline-flex items-center gap-1 mb-1"
          >
            <ArrowLeft className="w-3 h-3" /> Back to project
          </button>
          <div className="flex items-center justify-between gap-4">
            <div>
              <h1 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
                <Layout className="w-5 h-5 text-blue-600" />
                Section config
                {project && (
                  <span className="text-sm text-slate-500 font-normal">
                    · {project.name}
                  </span>
                )}
              </h1>
              <p className="text-xs text-slate-500 mt-0.5">
                Select which section types apply to this project. Keywords are
                used by the classifier to detect each section during analysis.
              </p>
            </div>
            <Button size="sm" onClick={openCreate} className="shrink-0">
              <Plus className="w-4 h-4 mr-1" />
              Custom section
            </Button>
          </div>
        </div>
      )}

      <div className={compact ? "space-y-4" : "p-6 max-w-4xl mx-auto space-y-6"}>
        {error && (
          <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2 flex justify-between">
            {error}
            <button onClick={() => setError(null)} className="ml-2">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
        {successMsg && (
          <div className="text-sm text-green-700 bg-green-50 border border-green-200 rounded px-3 py-2 flex justify-between">
            {successMsg}
            <button onClick={() => setSuccessMsg(null)} className="ml-2">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* project config section */}
        <section>
          <h2 className="text-sm font-semibold text-slate-700 mb-2">
            This project's sections
            <span className="ml-2 text-slate-400 font-normal">
              ({configs.length})
            </span>
          </h2>
          {loading ? (
            <p className="text-sm text-slate-400">Loading…</p>
          ) : configs.length === 0 ? (
            <div className="border border-dashed rounded-lg p-8 text-center text-slate-400 text-sm">
              No sections configured yet.
              <br />
              Add from the registry below or create a custom section.
            </div>
          ) : (
            <div className="space-y-2">
              {configs.map((entry) => (
                <ConfigRow
                  key={entry.id}
                  entry={entry}
                  departments={departments}
                  isInRegistry={registryCodes.has(entry.code)}
                  onEdit={openEdit}
                  onDelete={handleDelete}
                  onPushToRegistry={handlePushToRegistry}
                />
              ))}
            </div>
          )}
        </section>

        {/* registry browser */}
        <section>
          <button
            onClick={() => setRegistryOpen((v) => !v)}
            className="w-full flex items-center justify-between text-sm font-semibold text-slate-700 mb-2 hover:text-slate-900"
          >
            <span>
              Workspace registry
              <span className="ml-2 text-slate-400 font-normal">
                ({registry.length} entries)
              </span>
            </span>
            {registryOpen ? (
              <ChevronUp className="w-4 h-4 text-slate-400" />
            ) : (
              <ChevronDown className="w-4 h-4 text-slate-400" />
            )}
          </button>

          {registryOpen && (
            <div className="space-y-2">
              <div className="relative">
                <Search className="absolute left-2.5 top-2 w-4 h-4 text-slate-400" />
                <Input
                  value={registrySearch}
                  onChange={(e) => setRegistrySearch(e.target.value)}
                  placeholder="Filter registry…"
                  className="pl-8 h-8 text-sm"
                />
              </div>
              {filteredRegistry.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-4">
                  {registry.length === 0
                    ? "Registry is empty. Create entries via Admin → Section Registry."
                    : "No matching entries."}
                </p>
              ) : (
                filteredRegistry.map((entry) => (
                  <RegistryRow
                    key={entry.id}
                    entry={entry}
                    departments={departments}
                    alreadyAdded={configCodes.has(entry.code)}
                    onAdd={handleAddFromRegistry}
                  />
                ))
              )}
            </div>
          )}
        </section>
      </div>

      {/* create / edit dialog */}
      <Dialog
        open={dialogOpen}
        onOpenChange={(v) => {
          if (!v) {
            setDialogOpen(false);
            setEditingEntry(null);
            setError(null);
          }
        }}
      >
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingEntry ? "Edit section" : "Add custom section"}
            </DialogTitle>
          </DialogHeader>
          <SectionForm
            initial={
              editingEntry
                ? {
                    code: editingEntry.code,
                    name: editingEntry.name,
                    description: editingEntry.description ?? "",
                    keyword_patterns: editingEntry.keyword_patterns ?? [],
                    dept_id: editingEntry.dept_id ?? "",
                    secondary_dept_ids: editingEntry.secondary_dept_ids ?? [],
                  }
                : undefined
            }
            departments={departments}
            loading={saving}
            onSave={handleSave}
            onCancel={() => {
              setDialogOpen(false);
              setEditingEntry(null);
            }}
          />
          {error && (
            <p className="text-xs text-red-600 mt-1">{error}</p>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
